import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  Search, X, User, FileText, Users, Calendar,
  Loader2, ArrowRight, Sparkles, TrendingUp, Hash
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useSuggestions } from '@/hooks/useSearch';
import { search, searchTrending } from '@/lib/api';
import { getImageUrl } from '@/lib/imageUtils';

// Using design system colors: rose as primary, violet as secondary accent
const TYPE_CONFIG = {
  user: { 
    icon: User, 
    label: 'Person', 
    bg: 'bg-gathrr-400/10 dark:bg-gathrr-400/20',
    text: 'text-gathrr-500 dark:text-gathrr-400',
    iconBg: 'bg-gathrr-500 dark:bg-gathrr-400'
  },
  post: { 
    icon: FileText, 
    label: 'Post', 
    bg: 'bg-violet-500/10 dark:bg-violet-500/20',
    text: 'text-violet-600 dark:text-violet-400',
    iconBg: 'bg-violet-600 dark:bg-violet-500'
  },
  gathering: { 
    icon: Users, 
    label: 'Cause', 
    bg: 'bg-gathrr-400/10 dark:bg-gathrr-400/20',
    text: 'text-gathrr-500 dark:text-gathrr-400',
    iconBg: 'bg-gathrr-500 dark:bg-gathrr-400'
  },
  event: { 
    icon: Calendar, 
    label: 'Event', 
    bg: 'bg-amber-500/10 dark:bg-amber-500/20',
    text: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-600 dark:bg-amber-500'
  },
};

const SearchResultItem = ({ result, isSelected, onClick }) => {
  const config = TYPE_CONFIG[result.type] || TYPE_CONFIG.post;
  const Icon = config.icon;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        'group w-full flex items-center gap-4 p-3 rounded-xl text-left transition-all duration-200',
        isSelected 
          ? 'bg-gathrr-50 dark:bg-gathrr-400/10 ring-1 ring-gathrr-200 dark:ring-gathrr-400/20' 
          : 'hover:bg-stone-50 dark:hover:bg-zinc-800'
      )}
      data-testid={`search-result-${result.id}`}
    >
      {/* Avatar / Icon */}
      {result.type === 'user' && result.metadata?.avatar_url ? (
        <Avatar className={cn(
          "w-11 h-11 ring-2 ring-offset-2 ring-offset-white dark:ring-offset-zinc-900 transition-all",
          isSelected ? "ring-gathrr-400/50" : "ring-stone-200 dark:ring-zinc-700"
        )}>
          <AvatarImage src={getImageUrl(result.metadata.avatar_url)} />
          <AvatarFallback className={cn('text-white font-semibold', config.iconBg)}>
            {result.title?.charAt(0)?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className={cn(
          'w-11 h-11 rounded-xl flex items-center justify-center transition-transform duration-200',
          config.iconBg,
          isSelected && 'scale-105'
        )}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      )}
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            "font-semibold text-sm truncate transition-colors",
            isSelected ? "text-gathrr-600 dark:text-gathrr-400" : "text-stone-900 dark:text-white"
          )}>
            {result.title}
          </span>
        </div>
        
        <div className="flex items-center gap-2 mt-0.5">
          {result.subtitle && (
            <span className="text-xs text-stone-500 dark:text-zinc-500 truncate">
              {result.subtitle}
            </span>
          )}
          <span className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide',
            config.bg, config.text
          )}>
            {config.label}
          </span>
        </div>
        
        {result.snippet && !result.subtitle && (
          <p
            className="text-xs text-stone-500 dark:text-zinc-500 truncate mt-0.5"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.snippet) }}
          />
        )}
      </div>
      
      {/* Arrow indicator */}
      <div className={cn(
        'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
        isSelected 
          ? 'bg-gathrr-500 text-white' 
          : 'bg-stone-100 dark:bg-zinc-800 text-stone-400 dark:text-zinc-500 opacity-0 group-hover:opacity-100'
      )}>
        <ArrowRight className="w-4 h-4" />
      </div>
    </button>
  );
};

const TypeFilterPill = ({ type, count, isActive, onClick, config }) => {
  const Icon = config.icon;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200',
        isActive 
          ? 'bg-gathrr-500 text-white shadow-sm'
          : 'bg-white border border-stone-200 dark:bg-zinc-800 dark:border-white/10 text-stone-600 dark:text-zinc-400 hover:bg-stone-200 dark:hover:bg-zinc-700'
      )}
    >
      <Icon className="w-3 h-3" />
      {config.label}
      <span className={cn(
        'px-1.5 py-0.5 rounded-full text-[10px] font-bold',
        isActive 
          ? 'bg-white/20' 
          : 'bg-stone-200 dark:bg-zinc-700 text-stone-500 dark:text-zinc-400'
      )}>
        {count}
      </span>
    </button>
  );
};

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState({ types: {} });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeTypes, setActiveTypes] = useState(null);
  const [trending, setTrending] = useState([]);
  
  const { suggestions } = useSuggestions(query, query.length >= 2 && results.length === 0);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      loadTrending();
    } else {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setActiveTypes(null);
    }
  }, [open]);

  const loadTrending = async () => {
    try {
      const res = await searchTrending(null, 5);
      setTrending(res.data.trending || []);
    } catch (err) {
      console.error('Failed to load trending:', err);
    }
  };

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setFacets({ types: {} });
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const params = { q: query, limit: 10 };
        if (activeTypes) params.types = activeTypes.join(',');
        
        const res = await search(params);
        setResults(res.data.results || []);
        setFacets(res.data.facets || { types: {} });
        setSelectedIndex(0);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query, activeTypes]);

  const handleKeyDown = useCallback((e) => {
    const items = results.length > 0 ? results : (query.length < 2 ? trending : suggestions);
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => (i + 1) % Math.max(items.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => (i - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1));
    } else if (e.key === 'Enter' && items[selectedIndex]) {
      e.preventDefault();
      handleSelect(items[selectedIndex]);
    } else if (e.key === 'Escape') {
      onOpenChange(false);
    }
  }, [results, suggestions, trending, selectedIndex, query, onOpenChange]);

  const handleSelect = (item) => {
    onOpenChange(false);
    const type = item.type;
    if (type === 'user') {
      const username = item.metadata?.username || item.subtitle?.replace('@', '');
      if (username) navigate(`/profile/${username}`);
    } else if (type === 'gathering') {
      navigate('/spaces');
    } else if (type === 'event') {
      navigate('/spaces');
    } else {
      navigate('/');
    }
  };

  const toggleTypeFilter = (type) => {
    setActiveTypes(prev => {
      if (!prev) return [type];
      if (prev.includes(type)) {
        const newTypes = prev.filter(t => t !== type);
        return newTypes.length === 0 ? null : newTypes;
      }
      return [...prev, type];
    });
    setSelectedIndex(0);
  };

  const displayItems = results.length > 0 
    ? results 
    : (query.length < 2 ? trending : suggestions);

  const hasFilters = Object.keys(facets.types).some(k => facets.types[k] > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-xl p-0 gap-0 bg-white dark:bg-zinc-900 border border-stone-200 dark:border-zinc-700 shadow-xl overflow-hidden rounded-2xl [&>button]:hidden"
        aria-describedby="search-description"
      >
        <DialogTitle className="sr-only">Search FamCentral</DialogTitle>
        <p id="search-description" className="sr-only">Search for users, posts, gatherings, and events</p>
        
        {/* Search Input */}
        <div className="p-4 border-b border-stone-100 dark:border-zinc-800">
          <div className={cn(
            "flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200",
            "bg-stone-50 dark:bg-zinc-800",
            "border-stone-200 dark:border-zinc-700",
            "focus-within:border-stone-300 dark:focus-within:border-zinc-600"
          )}>
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-gathrr-500 animate-spin flex-shrink-0" />
            ) : (
              <Search className="w-5 h-5 text-stone-400 dark:text-zinc-500 flex-shrink-0" />
            )}
            
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search people, posts, gatherings..."
              className="search-input-no-ring flex-1 bg-transparent text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-zinc-500 text-sm outline-none border-none"
              data-testid="command-palette-input"
            />
            
            {query && (
              <button 
                onClick={() => setQuery('')}
                className="p-1.5 hover:bg-stone-200 dark:hover:bg-zinc-700 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4 text-stone-400 dark:text-zinc-500" />
              </button>
            )}
            
            <kbd className="hidden sm:inline-flex px-2 py-1 rounded-lg bg-stone-200 dark:bg-zinc-700 text-[10px] font-medium text-stone-500 dark:text-zinc-400">
              ESC
            </kbd>
          </div>
        </div>

        {/* Type Filters */}
        {hasFilters && (
          <div className="px-4 py-3 border-b border-stone-100 dark:border-zinc-800">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              <span className="text-xs font-medium text-stone-400 dark:text-zinc-500 flex-shrink-0">Filter:</span>
              {Object.entries(facets.types).map(([type, count]) => {
                const config = TYPE_CONFIG[type];
                if (!config || count === 0) return null;
                const isActive = !activeTypes || activeTypes.includes(type);
                
                return (
                  <TypeFilterPill
                    key={type}
                    type={type}
                    count={count}
                    isActive={isActive}
                    onClick={() => toggleTypeFilter(type)}
                    config={config}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Results */}
        <div className="max-h-[380px] overflow-y-auto p-3">
          {/* Trending Header */}
          {query.length < 2 && trending.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 mb-1">
              <TrendingUp className="w-4 h-4 text-gathrr-500" />
              <span className="text-xs font-semibold text-stone-600 dark:text-zinc-300 uppercase tracking-wider">
                Trending
              </span>
            </div>
          )}

          {/* Empty States */}
          {displayItems.length === 0 && query.length >= 2 && !isLoading && (
            <div className="text-center py-12 px-8">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-stone-100 dark:bg-zinc-800 flex items-center justify-center">
                <Search className="w-6 h-6 text-stone-400 dark:text-zinc-500" />
              </div>
              <p className="text-sm font-semibold text-stone-700 dark:text-zinc-300">No results found</p>
              <p className="text-xs text-stone-500 dark:text-zinc-500 mt-1">Try a different search term</p>
            </div>
          )}

          {displayItems.length === 0 && query.length < 2 && trending.length === 0 && !isLoading && (
            <div className="text-center py-12 px-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gathrr-50 dark:bg-gathrr-400/10 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-gathrr-500 dark:text-gathrr-400" />
              </div>
              <p className="text-base font-semibold text-stone-800 dark:text-white">Search FamCentral</p>
              <p className="text-sm text-stone-500 dark:text-zinc-400 mt-1">
                Find people, posts, gatherings and more
              </p>
              
              {/* Quick search suggestions */}
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {['wellness', 'tech', 'creative', 'local'].map(term => (
                  <button
                    key={term}
                    onClick={() => setQuery(term)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-stone-200 dark:bg-zinc-800 dark:border-white/10 text-xs font-medium text-stone-600 dark:text-zinc-400 hover:bg-stone-200 dark:hover:bg-zinc-700 transition-colors"
                  >
                    <Hash className="w-3 h-3" />
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results List */}
          <div className="space-y-1">
            {displayItems.map((item, index) => (
              <SearchResultItem
                key={item.id}
                result={item}
                isSelected={index === selectedIndex}
                onClick={() => handleSelect(item)}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 bg-stone-50 dark:bg-zinc-800/50 border-t border-stone-100 dark:border-zinc-800">
          <div className="flex items-center justify-between text-[11px] text-stone-400 dark:text-zinc-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-zinc-700 border border-stone-200 dark:border-zinc-600 font-mono text-[10px]">↑</kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-zinc-700 border border-stone-200 dark:border-zinc-600 font-mono text-[10px]">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-zinc-700 border border-stone-200 dark:border-zinc-600 font-mono text-[10px]">↵</kbd>
                <span className="ml-1">Select</span>
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
